<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { ThreadMessage } from '../stores/threads'
import { useWikiMarkdown } from '../composables/useWikiMarkdown'
import { useInternalLinkClicks } from '../composables/useInternalLinkClicks'
import CitationHoverManager from './CitationHoverManager.vue'
import ThreadAnswerTurn from './ThreadAnswerTurn.vue'

// The message half of ThreadDrawer.vue (#1102): user turns as
// right-aligned bubbles, assistant turns as serif answers
// (ThreadAnswerTurn), and the streaming status line. The composer
// (including its Stop control), the submit lifecycle and the
// recent-threads list stay in the parent.
const props = defineProps<{
  messages: ThreadMessage[]
  submitting: boolean
  elapsedSeconds: number
}>()

const emit = defineEmits<{
  (e: 'open-citation', path: string): void
  (e: 'close'): void
  (e: 'add-source'): void
}>()

const wikiMarkdown = useWikiMarkdown()
const bubblesEl = ref<HTMLElement | null>(null)

// Keep the newest turn in view as turns land. scrollIntoView reaches
// whichever ancestor scrolls (the drawer body), so the list itself
// doesn't need a fixed-height scroller of its own.
watch(
  () => props.messages.length,
  async () => {
    await nextTick()
    const last = bubblesEl.value?.lastElementChild as HTMLElement | null | undefined
    last?.scrollIntoView?.({ block: 'end' })
  },
)

function isStreaming(idx: number): boolean {
  return props.submitting && idx === props.messages.length - 1
}

// "Writing answer" once the assistant turn has text; before the first
// delta the brain is still walking the wiki.
const statusLabel = computed(() => {
  const last = props.messages.at(-1)
  return last?.role === 'assistant' && last.content.length > 0 ? 'Writing answer' : 'Reading the wiki'
})

// User turns are verbatim input — plain render, no citations to inject.
function renderUser(content: string): string {
  return wikiMarkdown.render(content, 'index.md')
}

const onBubbleClick = useInternalLinkClicks(() => emit('close'))
</script>

<template>
  <!-- Wrapped in CitationHoverManager so any [n] citation marker
       rendered into an answer gets a hover-card preview without
       per-turn wiring. -->
  <CitationHoverManager>
    <div
      ref="bubblesEl"
      class="flex flex-col gap-6 pb-2"
      data-testid="thread-bubbles"
      aria-live="polite"
      @click="onBubbleClick"
    >
      <template v-for="(msg, idx) in props.messages" :key="idx">
        <div
          v-if="msg.role === 'user'"
          class="max-w-[85%] self-end rounded-[14px_14px_4px_14px] border border-border-subtle bg-surface-hover px-4 py-3 text-[15px] leading-normal text-ink"
          data-testid="thread-user-turn"
        >
          <article class="wiki-markdown [&>:first-child]:mt-0 [&>:last-child]:mb-0" v-html="renderUser(msg.content)" />
        </div>
        <ThreadAnswerTurn
          v-else
          :message="msg"
          :streaming="isStreaming(idx)"
          @open-citation="emit('open-citation', $event)"
          @add-source="emit('add-source')"
        />
      </template>
      <div
        v-if="submitting"
        class="flex items-center gap-2 pl-[42px] text-[13px] text-ink-muted"
        data-testid="thread-thinking"
        role="status"
      >
        <span aria-hidden="true" class="h-2 w-2 animate-pulse rounded-full bg-brand-primary" />
        <span>{{ statusLabel }} · {{ elapsedSeconds }}s</span>
      </div>
    </div>
  </CitationHoverManager>
</template>
